import { padIbge, resolveCityUfKey } from "./concessionSheet";
import { mergeCepIntervals } from "./concessionCepExport";
import { auditCepRanges, padCep8 } from "./cepRangeAudit";

function ownerKey(region, target) {
  return `${String(region || "").trim()}|${String(target || "").trim()}`;
}

function claimKey(c) {
  return ownerKey(c.region, c.target);
}

function pushSpan(map, key, start, end) {
  if (!key) return;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return;
  if (!map.has(key)) map.set(key, []);
  map.get(key).push({ start, end });
}

function indexMunByIbge(municipalityRanges) {
  const map = new Map();
  for (const m of municipalityRanges || []) {
    const ibge = padIbge(m.ibge);
    const start = Number(m.start ?? m.start_cep);
    const end = Number(m.end ?? m.end_cep);
    pushSpan(map, ibge, start, end);
  }
  return map;
}

function indexMunByCityUf(municipalityRanges) {
  const map = new Map();
  for (const m of municipalityRanges || []) {
    const key = resolveCityUfKey(m.city, m.uf);
    const start = Number(m.start ?? m.start_cep);
    const end = Number(m.end ?? m.end_cep);
    pushSpan(map, key, start, end);
  }
  return map;
}

function spansForSheetRow(row, munByIbge, munByCityUf) {
  const ibge = padIbge(row.ibge);
  const byCode = ibge ? munByIbge.get(ibge) : null;
  if (byCode?.length) return { ibge, spans: byCode };
  const key = resolveCityUfKey(row.city, row.uf);
  const byName = key ? munByCityUf.get(key) : null;
  if (byName?.length) return { ibge: ibge || "", spans: byName };
  return { ibge, spans: null };
}

function summarizeClaim(c) {
  return {
    region: c.region,
    target: c.target,
    dealer: c.dealer || "",
    district: c.district || "",
    ibge: c.ibge || "",
    city: c.city || "",
    uf: c.uf || "",
    start: c.start,
    end: c.end,
  };
}

/** Intervalo menor (mais específico) vence; empate estável por IBGE / marca / filial. */
export function compareClaims(a, b) {
  const sa = a.end - a.start;
  const sb = b.end - b.start;
  if (sa !== sb) return sa - sb;
  const ibge = String(a.ibge || "").localeCompare(String(b.ibge || ""), "en");
  if (ibge !== 0) return ibge;
  const dealer = String(a.dealer || "").localeCompare(String(b.dealer || ""), "pt");
  if (dealer !== 0) return dealer;
  const district = String(a.district || "").localeCompare(String(b.district || ""), "pt");
  if (district !== 0) return district;
  return claimKey(a).localeCompare(claimKey(b), "en");
}

export function pickOwner(activeClaims) {
  const list = (activeClaims || []).filter(Boolean);
  if (!list.length) return { owner: null, conflict: false, winner: null, losers: [] };

  const byOwner = new Map();
  for (const c of list) {
    const k = claimKey(c);
    if (!byOwner.has(k)) byOwner.set(k, []);
    byOwner.get(k).push(c);
  }
  if (byOwner.size === 1) {
    const only = list[0];
    return {
      owner: { region: only.region, target: only.target },
      conflict: false,
      winner: only,
      losers: [],
    };
  }

  let best = null;
  for (const group of byOwner.values()) {
    const claim = group.slice().sort(compareClaims)[0];
    if (!best || compareClaims(claim, best) < 0) best = claim;
  }
  return {
    owner: { region: best.region, target: best.target },
    conflict: true,
    winner: best,
    losers: list.filter((c) => claimKey(c) !== claimKey(best)),
  };
}

/** Recorta `base` removendo todos os `cutters` (intervalos inclusivos). */
export function subtractIntervals(base, cutters) {
  const start = Number(base?.start ?? base?.start_cep);
  const end = Number(base?.end ?? base?.end_cep);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];

  let pieces = [{ start, end }];
  for (const cut of mergeCepIntervals(cutters || [])) {
    const next = [];
    for (const p of pieces) {
      if (cut.end < p.start || cut.start > p.end) {
        next.push(p);
        continue;
      }
      if (cut.start > p.start) {
        next.push({ start: p.start, end: Math.min(p.end, cut.start - 1) });
      }
      if (cut.end < p.end) {
        next.push({ start: Math.max(p.start, cut.end + 1), end: p.end });
      }
    }
    pieces = next.filter((x) => x.end >= x.start);
  }
  return pieces;
}

function mergeAssigned(segments) {
  const byOwner = new Map();
  for (const s of segments || []) {
    const k = ownerKey(s.region, s.target);
    if (!byOwner.has(k)) byOwner.set(k, { region: s.region, target: s.target, spans: [] });
    byOwner.get(k).spans.push({ start: s.start, end: s.end });
  }
  const out = [];
  for (const item of byOwner.values()) {
    for (const span of mergeCepIntervals(item.spans)) {
      out.push({ region: item.region, target: item.target, start: span.start, end: span.end });
    }
  }
  return out.sort(
    (a, b) =>
      String(a.region).localeCompare(String(b.region), "en") ||
      a.start - b.start ||
      String(a.target).localeCompare(String(b.target), "en")
  );
}

function mergeConflicts(conflicts) {
  const list = (conflicts || [])
    .slice()
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const out = [];
  for (const cur of list) {
    const last = out[out.length - 1];
    const sameWinner =
      last &&
      ownerKey(last.winner.region, last.winner.target) ===
        ownerKey(cur.winner.region, cur.winner.target);
    const lastLosers = (last?.losers || [])
      .map((l) => ownerKey(l.region, l.target))
      .sort()
      .join(",");
    const curLosers = (cur.losers || [])
      .map((l) => ownerKey(l.region, l.target))
      .sort()
      .join(",");
    if (last && sameWinner && lastLosers === curLosers && cur.start <= last.end + 1) {
      last.end = Math.max(last.end, cur.end);
      continue;
    }
    out.push({
      start: cur.start,
      end: cur.end,
      winner: cur.winner,
      losers: cur.losers,
    });
  }
  return out;
}

/**
 * Sweep-line: cada CEP fica com no máximo um (marca, destino).
 * Conflito entre destinos diferentes é recortado; o intervalo menor vence.
 */
export function assignNonOverlappingClaims(claims) {
  const events = [];
  (claims || []).forEach((c, i) => {
    events.push({ pos: c.start, kind: 1, claim: c, i });
    events.push({ pos: c.end + 1, kind: 0, claim: c, i });
  });
  events.sort((a, b) => a.pos - b.pos || a.kind - b.kind || a.i - b.i);

  const active = new Set();
  const assigned = [];
  const conflicts = [];
  let prevPos = null;
  let i = 0;
  while (i < events.length) {
    const pos = events[i].pos;
    if (prevPos != null && pos > prevPos && active.size) {
      const picked = pickOwner([...active]);
      if (picked.owner) {
        assigned.push({ start: prevPos, end: pos - 1, ...picked.owner });
        if (picked.conflict) {
          conflicts.push({
            start: prevPos,
            end: pos - 1,
            winner: summarizeClaim(picked.winner),
            losers: picked.losers.map(summarizeClaim),
          });
        }
      }
    }
    while (i < events.length && events[i].pos === pos) {
      if (events[i].kind === 0) active.delete(events[i].claim);
      else active.add(events[i].claim);
      i += 1;
    }
    prevPos = pos;
  }

  return {
    assigned: mergeAssigned(assigned),
    conflicts: mergeConflicts(conflicts),
  };
}

function classifySheetRows({ sheetRows, dealerMap, districtMap, munByIbge, munByCityUf }) {
  const claims = [];
  const skipNoRegion = [];
  const skipNoTarget = [];
  const skipNoIbge = [];
  const appliedCities = [];

  for (const row of sheetRows || []) {
    const region = String(dealerMap?.[row.dealer] || "").trim();
    if (!region) {
      skipNoRegion.push(row);
      continue;
    }
    const target = String(districtMap?.[row.district] || "").trim();
    if (!target) {
      skipNoTarget.push(row);
      continue;
    }
    const found = spansForSheetRow(row, munByIbge, munByCityUf);
    if (!found.spans?.length) {
      skipNoIbge.push(row);
      continue;
    }
    appliedCities.push({ ...row, region, target, ibge: found.ibge });
    for (const span of found.spans) {
      claims.push({
        start: span.start,
        end: span.end,
        region,
        target,
        dealer: row.dealer || "",
        district: row.district || "",
        ibge: found.ibge,
        city: row.city || "",
        uf: row.uf || "",
      });
    }
  }

  return { claims, skipNoRegion, skipNoTarget, skipNoIbge, appliedCities };
}

function toRangeRow(item, ordem, extra = {}) {
  return {
    region_key: item.region,
    start_cep: padCep8(item.start),
    end_cep: padCep8(item.end),
    target_cod_interno: item.target,
    status: 1,
    ordem,
    ...extra,
  };
}

function existingRangeBounds(r) {
  const start = Number(String(r.start_cep || r.start || "").replace(/\D+/g, ""));
  const end = Number(String(r.end_cep || r.end || "").replace(/\D+/g, ""));
  return { start, end };
}

/**
 * Monta o cadastro de faixas a partir da planilha, sem sobreposição.
 *
 * - Só entra município com workspace + empresa (filial).
 * - Faixas IBGE da mesma marca/destino são unidas.
 * - Cruzamento entre destinos diferentes é recortado (intervalo menor vence).
 * - Marcas aplicadas substituem as faixas atuais dessas marcas.
 * - Faixas de marcas de fora permanecem, recortadas se cruzarem as novas.
 * - O conjunto final passa pela mesma auditoria que bloqueia o save.
 */
export function buildConcessionCepApply({
  sheetRows = [],
  dealerMap = {},
  districtMap = {},
  municipalityRanges = [],
  existingRanges = [],
  regionKeys,
  targetSlugsByRegion,
} = {}) {
  const munByIbge = indexMunByIbge(municipalityRanges);
  const munByCityUf = indexMunByCityUf(municipalityRanges);
  const classified = classifySheetRows({
    sheetRows,
    dealerMap,
    districtMap,
    munByIbge,
    munByCityUf,
  });
  const { assigned, conflicts } = assignNonOverlappingClaims(classified.claims);

  const appliedRegions = new Set(assigned.map((a) => a.region));
  const newCutters = assigned.map((a) => ({ start: a.start, end: a.end }));

  const kept = [];
  const droppedApplied = [];
  const carvedKept = [];

  for (const raw of existingRanges || []) {
    const region = String(raw.region_key || "").trim();
    const { start, end } = existingRangeBounds(raw);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
      droppedApplied.push(raw);
      continue;
    }
    if (appliedRegions.has(region)) {
      droppedApplied.push(raw);
      continue;
    }
    const leftovers = subtractIntervals({ start, end }, newCutters);
    if (!leftovers.length) {
      carvedKept.push(raw);
      continue;
    }
    if (leftovers.length === 1 && leftovers[0].start === start && leftovers[0].end === end) {
      kept.push({ ...raw, start, end });
      continue;
    }
    carvedKept.push(raw);
    for (const piece of leftovers) {
      kept.push({
        ...raw,
        start: piece.start,
        end: piece.end,
        start_cep: padCep8(piece.start),
        end_cep: padCep8(piece.end),
      });
    }
  }

  const nextRanges = [];
  for (const item of assigned) {
    nextRanges.push(toRangeRow(item, nextRanges.length));
  }
  for (const item of kept) {
    nextRanges.push(
      toRangeRow(
        {
          region: item.region_key,
          target: item.target_cod_interno,
          start: item.start,
          end: item.end,
        },
        nextRanges.length,
        {
          _id: item._id,
          id: item.id,
          status: Number(item.status) === 0 ? 0 : 1,
        }
      )
    );
  }

  const audit = auditCepRanges(nextRanges, {
    regionKeys: Array.isArray(regionKeys) ? regionKeys : undefined,
    targetSlugsByRegion:
      targetSlugsByRegion && typeof targetSlugsByRegion === "object" ? targetSlugsByRegion : undefined,
  });

  const blockingReasons = [];
  if (!classified.appliedCities.length) {
    blockingReasons.push(
      "Nenhum município tem workspace e empresa (filial) associados, com faixa IBGE de CEP."
    );
  }
  if (audit.hasBlocking) {
    blockingReasons.push("A auditoria ainda encontrou faixas sobrepostas ou inválidas.");
  }

  const uniqueNames = (rows, key) =>
    [...new Set((rows || []).map((r) => r[key]).filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b), "pt")
    );

  return {
    canApply: blockingReasons.length === 0,
    blockingReasons,
    nextRanges,
    assigned,
    conflicts,
    audit,
    appliedRegions: [...appliedRegions].sort((a, b) => a.localeCompare(b, "en")),
    summary: {
      sheet: (sheetRows || []).length,
      appliedCities: classified.appliedCities.length,
      newRanges: assigned.length,
      keptRanges: kept.length,
      droppedExisting: droppedApplied.length,
      carvedExisting: carvedKept.length,
      conflicts: conflicts.length,
      skipNoRegion: classified.skipNoRegion.length,
      skipNoTarget: classified.skipNoTarget.length,
      skipNoIbge: classified.skipNoIbge.length,
      missingIbge: classified.skipNoIbge.length,
    },
    skipped: {
      noRegion: uniqueNames(classified.skipNoRegion, "dealer"),
      noTarget: uniqueNames(classified.skipNoTarget, "district"),
      noIbge: classified.skipNoIbge.length,
    },
  };
}
